import React, { useContext, useMemo, useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer } from '@mui/x-data-grid-pro'
import { lockedReducer } from '../Utils/reducers'
import { externalComponent } from '../AppRoutes'
import TableToolbar from './toolbar'
import MobileTable from './mobileTable'
import { LicenseInfo } from '@mui/x-license';
import { useGridApiRef } from '@mui/x-data-grid-pro'

LicenseInfo.setLicenseKey(process.env.REACT_APP_LICENSE_KEY_MUI);


const getData = async (state, setLoader, setState) => {
    setLoader(true)


    const searchParams = new URLSearchParams();

    //pagination
    searchParams.set('limit', state.paginationModel.pageSize)
    searchParams.set('offset', (state.paginationModel.page) * state.paginationModel.pageSize)

    if (state.mobileFilter)
        searchParams.set('offset', 0)

    //filter
    Object.entries(state.filterModel.customItems).forEach(([key, value]) => {
        value && searchParams.set([key.replace('__equals', '')], value)
    })

    //or
    if (state.filterModel.or)
        searchParams.set('or', 'or')


    setLoader(false)
    const newState = {
        paginationModel: state.paginationModel,
        filterModel: { ...state.filterModel }
    }
    newState.ready = true
    setState(newState)
}

export const TableHook = () => {
    const table = useRef()
    const reloadData = table.reloadData || (() => { })
    return [table, reloadData]
}

export const TableHookPro = () => {
    const apiRef = useGridApiRef()
    const table = {
        apiRef: apiRef,
        reloadData: () => { },
    }
    return [table, table.reloadData]
}

const initState = {
    paginationModel: { page: 0, pageSize: 25 },
    filterModel: { items: [], customItems: {} },
    next: null,
    rowCount: 0,
    previous: null,
    ready: true,
}

const Table = ({ getURL, updateRows, innerRef, toolbar, mobile, mobileProps, checkbox = false, pro = false, nameTable = '', ...props }) => {

    const { setLoader, isMobile } = useContext(externalComponent)

    const gridApiRef = useGridApiRef()
    const regularRef = useRef()
    const apiRef = pro ? gridApiRef : regularRef

    const [state, setState] = useReducer(lockedReducer, {
        ...initState,
        getURL: getURL,
        columnState: props.columns || []
    })

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [initialStateLoaded, setInitialStateLoaded] = useState(false);

    if (innerRef) {
        innerRef.reloadData = (url, data = {}) => {
            getData(url || getURL, { ...state, ...data }, updateRows, setLoader, setState)
        }

        // Se for Pro, já vai vir com apiRef. Se não, cria um novo
        innerRef.apiRef = innerRef.apiRef || apiRef
        innerRef.state = state
    }

    const settoolbar = toolbar ? {
        slots: { ...props.slots, toolbar: TableToolbar }
    } : null

    const handleColumnWidthChange = useCallback((params) => {
        const savedWidths = JSON.parse(localStorage.getItem(nameTable) || '{}');
        savedWidths[params.colDef.field] = params.width;
        localStorage.setItem(nameTable, JSON.stringify(savedWidths));
    }, [nameTable]);

    const handleColumnOrderChange = () => {
        const currentOrder = innerRef.apiRef.current.getAllColumns()
            .filter((col) => col.visible !== false)
            .map((col) => col.field);

        if (nameTable !== '') {
            localStorage.setItem(nameTable, JSON.stringify({
                ...JSON.parse(localStorage.getItem(nameTable) || '{}'),
                order: currentOrder
            }));
        }
    };

    const handleColumnVisibilityChange = (newModel) => {
        setColumnVisibilityModel(newModel);
        const saved = JSON.parse(localStorage.getItem(`visibility-${nameTable}`)) || {};
        if (`visibility-${nameTable}` !== '') {
            localStorage.setItem(`visibility-${nameTable}`, JSON.stringify({
                ...saved,
                visibilityModel: newModel
            }));
        }
    };


    useEffect(() => {
        let saved = {};
        let visibilityModel = {};

        try {
            saved = JSON.parse(localStorage.getItem(nameTable)) || {};
        } catch {
            saved = {};
        }

        try {
            const rawVisibility = JSON.parse(localStorage.getItem(`visibility-${nameTable}`)) || {};
            visibilityModel = rawVisibility.visibilityModel || {}; // extrai do modelo salvo
        } catch {
            visibilityModel = {};
        }

        const savedWidths = saved || {};
        const savedOrder = saved.order || [];

        const originalCols = props.columns || [];

        const withProps = originalCols.map(col => {
            const width = typeof savedWidths[col.field] === 'number' ? savedWidths[col.field] : col.width;

            return {
                ...col,
                width,
                flex: width ? undefined : col.flex,
            };
        });

        const reorderedCols = [
            ...savedOrder.map(field => withProps.find(col => col.field === field)).filter(Boolean),
            ...withProps.filter(col => !savedOrder.includes(col.field))
        ];

        setState({ columnState: reorderedCols });
        setColumnVisibilityModel(visibilityModel); // aqui está o pulo do gato
        setInitialStateLoaded(true);
    }, [nameTable, props.columns]);


    const CustomToolbar = () => (
        <GridToolbarContainer
            sx={{
                '& .MuiButton-root': {
                    color: '#333',
                },
                '& .MuiButton-root svg': {
                    fill: '#333',
                },
            }}
        >

            <GridToolbarColumnsButton />

        </GridToolbarContainer>
    );


    if (!initialStateLoaded) return null;


    return (
        <>
            {(state.ready && (!isMobile || !mobile)) &&
                (pro ? (
                    <DataGridPro
                        checkboxSelection={checkbox}
                        {...props}
                        {...settoolbar}
                        columns={state.columnState || props.columns}
                        columnVisibilityModel={columnVisibilityModel}
                        pageSizeOptions={[25, 50, 100]}
                        apiRef={innerRef.apiRef}
                        keepNonExistentRowsSelected
                        onColumnWidthChange={handleColumnWidthChange}
                        onColumnOrderChange={handleColumnOrderChange}
                        onColumnVisibilityModelChange={handleColumnVisibilityChange}
                        slots={{
                            toolbar: () => (
                                <CustomToolbar />
                            ),
                        }}

                    />
                ) : (
                    <DataGrid
                        checkboxSelection={checkbox}
                        {...props}
                        {...settoolbar}
                        pageSizeOptions={[25, 50, 100]}
                        apiRef={apiRef}
                        keepNonExistentRowsSelected
                    />
                ))
            }

            {(state.ready && (isMobile && mobile)) &&
                <MobileTable
                    rows={props.rows}
                    parent={state}
                    setParent={setState}
                    onFilterModelChange={(e) => getData(state.getURL, { ...state, filterModel: e, mobileFilter: true }, updateRows, setLoader, setState)}
                    onPaginationModelChange={(e) => {
                        getData(state.getURL, { ...state, paginationModel: e, scroll: true }, updateRows, setLoader, setState, props.rows)
                    }}
                    columns={props.columns}
                    slots={props.slots}
                    getRowClassName={props.getRowClassName}
                    {...mobileProps}
                />}
        </>
    )
}

export default Table